/**
 * Timecard API Contract
 *
 * Defines the oRPC contract for Timecards - clock in/out, breaks, approvals, and change requests.
 *
 * IMPORTANT SECURITY NOTES:
 * - Company-scoped endpoints (dashboard, employeeView, active) do NOT include companyId
 *   in path or input - it comes from JWT context
 * - Authorization is handled by services (ApproverGroup membership), NOT permission middleware
 *
 * ROUTE ORDERING:
 * - Specific routes (/clock-in, /clock-out, /active, etc.) MUST come before /{id}
 *   to prevent matching as UUID
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { CreateTimecardInputSchema, UpdateEntryInputSchema, StartBreakInputSchema, EndBreakInputSchema, ReturnTimecardInputSchema, RestartApprovalInputSchema, FlagIssueInputSchema, ResolveIssueInputSchema, MarkAsPaidInputSchema, EditTimecardInputSchema, ChangeRequestInputSchema, ProcessChangeRequestInputSchema, TimecardDashboardQuerySchema, EmployeeTimecardQuerySchema, TimecardResponseSchema, TimecardSummarySchema, EmployeeTimecardSummarySchema, ActiveTimecardResponseSchema, TimecardAuditLogQuerySchema, TimecardAuditLogListResponseSchema, ManagerCreateTimecardInputSchema, TimecardAdjustmentInputSchema, TimecardAdjustmentResponseSchema, TimecardAdjustmentDetailSchema, GetTimecardAdjustmentsInputSchema, } from "@hourly/api/timecard";
import { ExportPayrollCsvResponseSchema } from "@hourly/api/payrollExport";
// ============================================
// Clock Operations (specific routes first)
// ============================================
/**
 * POST /timecards/clock-in
 * Clock in for a shift
 * Note: employeeId comes from JWT context via employment lookup
 */
const clockIn = oc
    .route({
    method: "POST",
    path: "/timecards/clock-in",
    summary: "Clock in for a shift",
    tags: ["Timecards"],
    successStatus: 201,
})
    .input(CreateTimecardInputSchema)
    .output(TimecardResponseSchema);
/**
 * POST /timecards/clock-out
 * Clock out from a shift
 * Note: employeeId comes from JWT context via employment lookup
 */
const clockOut = oc
    .route({
    method: "POST",
    path: "/timecards/clock-out",
    summary: "Clock out from a shift",
    tags: ["Timecards"],
})
    .input(UpdateEntryInputSchema)
    .output(TimecardResponseSchema);
/**
 * POST /timecards/start-break
 * Start a break
 * Note: employeeId comes from JWT context via employment lookup
 */
const startBreak = oc
    .route({
    method: "POST",
    path: "/timecards/start-break",
    summary: "Start a break",
    tags: ["Timecards"],
})
    .input(StartBreakInputSchema)
    .output(TimecardResponseSchema);
/**
 * POST /timecards/end-break
 * End a break
 * Note: employeeId comes from JWT context via employment lookup
 */
const endBreak = oc
    .route({
    method: "POST",
    path: "/timecards/end-break",
    summary: "End a break",
    tags: ["Timecards"],
})
    .input(EndBreakInputSchema)
    .output(TimecardResponseSchema);
// ============================================
// Query Operations (specific routes before /{id})
// ============================================
/**
 * GET /timecards/dashboard
 * Get manager dashboard with filters
 * Note: companyId comes from JWT context
 */
const getDashboard = oc
    .route({
    method: "GET",
    path: "/timecards/dashboard",
    summary: "Get manager dashboard",
    tags: ["Timecards"],
})
    .input(TimecardDashboardQuerySchema)
    .output(z.array(TimecardSummarySchema));
/**
 * GET /timecards/employee/{employeeId}
 * Get employee timecard view
 * Note: companyId comes from JWT context
 */
const getEmployeeView = oc
    .route({
    method: "GET",
    path: "/timecards/employee/{employeeId}",
    summary: "Get employee timecard view",
    tags: ["Timecards"],
})
    .input(z.object({ employeeId: z.uuid() }).merge(EmployeeTimecardQuerySchema))
    .output(EmployeeTimecardSummarySchema);
/**
 * GET /timecards/active
 * Get active timecard for current user
 * Note: employeeId comes from JWT context via employment lookup
 */
const getActive = oc
    .route({
    method: "GET",
    path: "/timecards/active",
    summary: "Get active timecard for current user",
    tags: ["Timecards"],
})
    .input(z.object({}))
    .output(ActiveTimecardResponseSchema);
const myTimecards = oc
    .route({
    method: "GET",
    path: "/timecards/my",
    summary: "Get my timecards (authenticated user)",
    tags: ["Timecards"],
})
    .input(z.object({ start: z.iso.datetime(), end: z.iso.datetime() }))
    .output(EmployeeTimecardSummarySchema);
/**
 * POST /timecards/from-shift/{shiftId}
 * Create timecard from shift
 * Note: employeeId comes from JWT context via employment lookup
 */
const createFromShift = oc
    .route({
    method: "POST",
    path: "/timecards/from-shift/{shiftId}",
    summary: "Create timecard from shift",
    tags: ["Timecards"],
    successStatus: 201,
})
    .input(z.object({ shiftId: z.uuid() }))
    .output(TimecardResponseSchema);
/**
 * POST /timecards/manager-create
 * Manager creates timecard from shift (approve or request employee)
 */
const managerCreateTimecard = oc
    .route({
    method: "POST",
    path: "/timecards/manager-create",
    summary: "Manager creates timecard from shift (approve or request employee)",
    tags: ["Timecards"],
    successStatus: 201,
})
    .input(ManagerCreateTimecardInputSchema)
    .output(TimecardResponseSchema);
/**
 * POST /timecards/audit-logs
 * Get audit logs for multiple timecards (batch fetch)
 * Authorization: User must own the timecard OR can approve for its location
 */
const getAuditLogs = oc
    .route({
    method: "POST",
    path: "/timecards/audit-logs",
    summary: "Get audit logs for timecards",
    tags: ["Timecards"],
})
    .input(TimecardAuditLogQuerySchema)
    .output(TimecardAuditLogListResponseSchema);
const myAuditLogs = oc
    .route({
    method: "POST",
    path: "/timecards/my/audit-logs",
    summary: "Get audit logs for my own timecards",
    tags: ["Timecards"],
})
    .input(TimecardAuditLogQuerySchema)
    .output(TimecardAuditLogListResponseSchema);
/**
 * POST /timecards/{id}/adjustments
 * Create a post-export adjustment for a timecard
 * Requires timecard:approve permission and timecard must be exportedToPayroll
 */
const createAdjustment = oc
    .route({
    method: "POST",
    path: "/timecards/{id}/adjustments",
    summary: "Create a post-export adjustment for a timecard",
    tags: ["Timecards"],
    successStatus: 201,
})
    .input(z.object({ id: z.uuid(), ...TimecardAdjustmentInputSchema.shape }))
    .output(TimecardAdjustmentResponseSchema);
const getAdjustments = oc
    .route({
    method: "POST",
    path: "/timecards/adjustments/query",
    summary: "Get adjustments for multiple timecards",
    tags: ["Timecards"],
})
    .input(GetTimecardAdjustmentsInputSchema)
    .output(z.array(TimecardAdjustmentDetailSchema));
/**
 * GET /timecards/export/payroll-csv
 * Export payroll data as CSV
 */
const exportPayrollCsv = oc
    .route({
    method: "GET",
    path: "/timecards/export/payroll-csv",
    summary: "Export payroll data as CSV",
    tags: ["Timecards"],
})
    .input(z.object({
    startDate: z.iso.datetime(),
    endDate: z.iso.datetime(),
    locationId: z.uuid().optional(),
    markAsExported: z.stringbool(),
}))
    .output(ExportPayrollCsvResponseSchema);
/**
 * GET /timecards/{id}
 * Get timecard by ID
 */
const getById = oc
    .route({
    method: "GET",
    path: "/timecards/{id}",
    summary: "Get timecard by ID",
    tags: ["Timecards"],
})
    .input(z.object({ id: z.uuid() }))
    .output(TimecardResponseSchema);
// ============================================
// Workflow Operations
// ============================================
/**
 * POST /timecards/{id}/submit
 * Submit a timecard for approval
 */
const submit = oc
    .route({
    method: "POST",
    path: "/timecards/{id}/submit",
    summary: "Submit a timecard for approval",
    tags: ["Timecards"],
})
    .input(z.object({ id: z.uuid() }))
    .output(TimecardResponseSchema);
const mySubmit = oc
    .route({
    method: "POST",
    path: "/timecards/my/{id}/submit",
    summary: "Submit my own timecard for approval",
    tags: ["Timecards"],
})
    .input(z.object({ id: z.uuid() }))
    .output(TimecardResponseSchema);
const myGetById = oc
    .route({
    method: "GET",
    path: "/timecards/my/{id}",
    summary: "Get my own timecard by ID",
    tags: ["Timecards"],
})
    .input(z.object({ id: z.uuid() }))
    .output(TimecardResponseSchema);
/**
 * POST /timecards/{id}/approve
 * Approve a timecard
 * Authorization: Must be in approver group (checked by service)
 */
const approve = oc
    .route({
    method: "POST",
    path: "/timecards/{id}/approve",
    summary: "Approve a timecard",
    tags: ["Timecards"],
})
    .input(z.object({ id: z.uuid() }))
    .output(TimecardResponseSchema);
/**
 * POST /timecards/{id}/edit
 * Edit timecard times, breaks, and shift
 * Authorization: Must be in approver group (checked by service)
 */
const edit = oc
    .route({
    method: "POST",
    path: "/timecards/{id}/edit",
    summary: "Edit timecard times, breaks, and shift",
    tags: ["Timecards"],
})
    .input(EditTimecardInputSchema.extend({ id: z.uuid() }))
    .output(TimecardResponseSchema);
/**
 * POST /timecards/{id}/return
 * Return a timecard for revision
 * Authorization: Must be in approver group (checked by service)
 */
const returnTimecard = oc
    .route({
    method: "POST",
    path: "/timecards/{id}/return",
    summary: "Return a timecard for revision",
    tags: ["Timecards"],
})
    .input(z.object({ id: z.uuid() }).merge(ReturnTimecardInputSchema))
    .output(TimecardResponseSchema);
/**
 * POST /timecards/{id}/flag-issue
 * Flag a timecard with an issue
 * Authorization: Must be in approver group (checked by service)
 */
const flagIssue = oc
    .route({
    method: "POST",
    path: "/timecards/{id}/flag-issue",
    summary: "Flag a timecard with an issue",
    tags: ["Timecards"],
})
    .input(z.object({ id: z.uuid() }).merge(FlagIssueInputSchema))
    .output(TimecardResponseSchema);
/**
 * POST /timecards/{id}/resolve-issue
 * Resolve a timecard issue
 * Authorization: Must be in approver group (checked by service)
 */
const resolveIssue = oc
    .route({
    method: "POST",
    path: "/timecards/{id}/resolve-issue",
    summary: "Resolve a timecard issue",
    tags: ["Timecards"],
})
    .input(z.object({ id: z.uuid() }).merge(ResolveIssueInputSchema))
    .output(TimecardResponseSchema);
/**
 * POST /timecards/{id}/mark-paid
 * Mark a timecard as paid (APPROVED → PAID)
 * Authorization: Must be in approver group (checked by service)
 */
const markAsPaid = oc
    .route({
    method: "POST",
    path: "/timecards/{id}/mark-paid",
    summary: "Mark an approved timecard as paid",
    tags: ["Timecards"],
})
    .input(z.object({ id: z.uuid() }).merge(MarkAsPaidInputSchema))
    .output(TimecardResponseSchema);
/**
 * POST /timecards/{id}/restart-approval
 * Restart the approval process for a timecard
 * Authorization: Must be in approver group (checked by service)
 */
const restartApproval = oc
    .route({
    method: "POST",
    path: "/timecards/{id}/restart-approval",
    summary: "Restart the approval process for a timecard",
    tags: ["Timecards"],
})
    .input(z.object({ id: z.uuid() }).merge(RestartApprovalInputSchema))
    .output(TimecardResponseSchema);
// ============================================
// Change Request Operations
// ============================================
/**
 * POST /timecards/change-requests
 * Submit a change request
 * Note: employeeId comes from JWT context via employment lookup
 */
const createChangeRequest = oc
    .route({
    method: "POST",
    path: "/timecards/change-requests",
    summary: "Submit batch change requests for timecard entries",
    tags: ["Timecards"],
    successStatus: 201,
})
    .input(ChangeRequestInputSchema)
    .output(TimecardResponseSchema);
/**
 * POST /timecards/change-requests/{id}/process
 * Process a change request (approve/reject)
 * Authorization: Must be in approver group (checked by service)
 */
const processChangeRequest = oc
    .route({
    method: "POST",
    path: "/timecards/change-requests/{id}/process",
    summary: "Process a change request (approve/reject)",
    tags: ["Timecards"],
})
    .input(z.object({ id: z.uuid() }).merge(ProcessChangeRequestInputSchema))
    .output(TimecardResponseSchema);
// ============================================
// Destructive Operations
// ============================================
/**
 * DELETE /timecards/{id}
 * Delete a timecard permanently
 * Authorization: Requires timecard:approve AND shift:approve permissions
 * Additional check: Must be in location's approver group (checked by service)
 */
const deleteTimecard = oc
    .route({
    method: "DELETE",
    path: "/timecards/{id}",
    summary: "Delete a timecard (requires timecard:approve + shift:approve)",
    tags: ["Timecards"],
})
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.literal(true) }));
/**
 * DELETE /timecards/{id}/with-shift
 * Delete a timecard and its underlying shift permanently
 * Authorization: Requires timecard:approve AND shift:approve permissions
 * Additional check: Must be in location's approver group (checked by service)
 */
const deleteTimecardWithShift = oc
    .route({
    method: "DELETE",
    path: "/timecards/{id}/with-shift",
    summary: "Delete a timecard and its shift (requires timecard:approve + shift:approve)",
    tags: ["Timecards"],
})
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.literal(true) }));
// ============================================
// Export Contract
// ============================================
export const timecardContract = {
    // Clock operations (specific routes before /{id})
    clockIn,
    clockOut,
    startBreak,
    endBreak,
    // Query operations (specific routes before /{id})
    getDashboard,
    getEmployeeView,
    getActive,
    myTimecards,
    createFromShift,
    managerCreateTimecard,
    getAuditLogs,
    exportPayrollCsv,
    getById,
    // Workflow operations
    submit,
    mySubmit,
    myGetById,
    myAuditLogs,
    approve,
    edit,
    createAdjustment,
    getAdjustments,
    return: returnTimecard,
    flagIssue,
    resolveIssue,
    markAsPaid,
    restartApproval,
    // Change requests
    createChangeRequest,
    processChangeRequest,
    // Destructive operations
    delete: deleteTimecard,
    deleteWithShift: deleteTimecardWithShift,
};
//# sourceMappingURL=timecard.js.map